import type { AbaDaPlanilha } from '@obraplan/core/domain/takeoff/planilha-de-indices';
import { decodeText, detectDelimiter, parseDelimited } from '@obraplan/table-reader/csv-reader';
import {
  looksLikeZip,
  PlanilhaGrandeDemais,
  readXlsxSheets,
} from '@obraplan/table-reader/xlsx-reader';

/**
 * O arquivo virado em abas — a parte da importação da planilha de serviços que não depende de
 * tela. Roda dentro do worker (`ler-planilha.worker.ts`) e, onde worker não há, na própria
 * thread (`ler-planilha.ts`).
 *
 * O formato é decidido pelos **bytes**, como no resto do sistema: planilha exportada de sistema
 * de compras chega com extensão que não corresponde ao conteúdo com frequência suficiente para
 * que confiar no nome seja um defeito esperando acontecer. O CSV entra como uma aba só, com o
 * nome do arquivo — ele não tem abas para separar.
 */
export function abasDosBytes(bytes: Uint8Array, nomeDoArquivo: string): readonly AbaDaPlanilha[] {
  if (looksLikeZip(bytes)) {
    return readXlsxSheets(bytes).map((aba) => ({ name: aba.sheetName, rows: aba.rows }));
  }
  if (pareceExcelAntigo(bytes)) throw new ArquivoRecusado(MENSAGEM_DO_EXCEL_ANTIGO);

  const texto = decodeText(bytes);
  // A linha só de separadores (`;;;;`) é a linha vazia do CSV: some, como a do XLSX.
  const linhas = parseDelimited(texto, detectDelimiter(texto)).filter((row) =>
    row.some((celula) => celula.trim() !== ''),
  );
  return [{ name: nomeDoArquivo, rows: linhas }];
}

/** O motivo da recusa, já na voz da tela. */
export class ArquivoRecusado extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ArquivoRecusado';
  }
}

const MENSAGEM_DO_EXCEL_ANTIGO =
  'Este arquivo é do Excel antigo (.xls), que o sistema não lê. Abra no Excel, use "Salvar como" e escolha "Pasta de Trabalho do Excel (.xlsx)".';

/** A assinatura do formato composto da Microsoft (`D0 CF 11 E0`), que é o `.xls` de antes de 2007. */
function pareceExcelAntigo(bytes: Uint8Array): boolean {
  return bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0;
}

export const MENSAGEM_DE_ARQUIVO_ILEGIVEL =
  'Não foi possível ler este arquivo. Exporte como XLSX ou CSV e tente de novo.';

/** O que a tela diz quando a leitura levanta: a recusa com nome diz o próprio motivo. */
export function mensagemDaFalha(causa: unknown): string {
  if (causa instanceof PlanilhaGrandeDemais || causa instanceof ArquivoRecusado) {
    return causa.message;
  }
  return MENSAGEM_DE_ARQUIVO_ILEGIVEL;
}

/** O pedido que a tela manda ao worker. */
export interface PedidoDaPlanilha {
  readonly bytes: Uint8Array;
  readonly nome: string;
}

/** A resposta do worker: sempre uma, e nunca o silêncio. */
export type RespostaDaPlanilha =
  | { readonly tipo: 'PRONTO'; readonly abas: readonly AbaDaPlanilha[] }
  | { readonly tipo: 'FALHOU'; readonly mensagem: string };
